import { useState, useEffect } from 'react';
import { Smartphone, ImageOff } from 'lucide-react';
import Modal from '@/components/ui/Modal';

// Convert a TTL in seconds to a human-readable string.
// Matches the inline pill formatting in NotificationItem so both surfaces
// describe the same number consistently.
function formatTtlHumanReadable(seconds: number): string {
  if (seconds < 60) return `${seconds} second${seconds === 1 ? '' : 's'}`;
  if (seconds < 3600) {
    const m = Math.round(seconds / 60);
    return `${m} minute${m === 1 ? '' : 's'}`;
  }
  if (seconds < 86400) {
    const h = Math.round(seconds / 3600);
    return `${h} hour${h === 1 ? '' : 's'}`;
  }
  const d = Math.round(seconds / 86400);
  return `${d} day${d === 1 ? '' : 's'}`;
}

interface PushDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  iconUrl?: string | null;
  ttl?: number | null;
}

/**
 * Shared modal that surfaces the push-specific metadata for a notification:
 * the icon image (full-size, with graceful fallback if the URL 404s) and
 * the Time To Live in human-readable form. Used from:
 *   - NotificationItem (My Notifications tab on the admin page)
 *   - NotificationHistory (Notification History tab on the admin page)
 *
 * Renders nothing meaningful when the notification has no push metadata —
 * call sites should only open this when the row is a push notification.
 */
export function PushDetailsModal({
  isOpen,
  onClose,
  title,
  iconUrl,
  ttl,
}: PushDetailsModalProps) {
  const [iconError, setIconError] = useState(false);

  // Reset the broken-image fallback when the icon URL itself changes — without
  // this, an earlier failure for a different notification would stick across
  // modal openings.
  useEffect(() => {
    setIconError(false);
  }, [iconUrl]);

  const hasIcon = !!iconUrl && !iconError;
  const hasTtl = typeof ttl === 'number' && ttl > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Push notification details" subtitle={title} size="md">
      <div className="space-y-5">
        <div className="flex items-center gap-3 text-sm text-gray-600">
          <Smartphone className="w-4 h-4 text-primary-600" />
          <span>Push-channel customization for this notification</span>
        </div>

        {/* Icon image */}
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
            Icon
          </p>
          {hasIcon ? (
            <div className="w-24 h-24 rounded-xl bg-gray-50 border border-gray-200 overflow-hidden flex items-center justify-center">
              <img
                src={iconUrl!}
                alt="Push icon"
                className="w-full h-full object-cover"
                onError={() => setIconError(true)}
              />
            </div>
          ) : (
            <div className="flex items-center gap-3 p-4 bg-gray-50 border border-dashed border-gray-200 rounded-lg text-sm text-gray-500">
              <ImageOff className="w-5 h-5 text-gray-400" />
              {iconUrl
                ? 'Icon URL was provided but failed to load.'
                : 'No icon was configured for this push.'}
            </div>
          )}
        </div>

        {/* Time To Live */}
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
            Time To Live
          </p>
          {hasTtl ? (
            <div className="flex items-baseline gap-2">
              <p className="text-2xl font-semibold text-gray-900">
                {formatTtlHumanReadable(ttl!)}
              </p>
              <p className="text-sm text-gray-500">({ttl!.toLocaleString()} s)</p>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              Not set — the OneSignal default (3 days) applies.
            </p>
          )}
          <p className="mt-2 text-xs text-gray-500">
            How long the push service queues the notification if the recipient's device is offline.
            After this window the push is silently dropped.
          </p>
        </div>
      </div>
    </Modal>
  );
}

export default PushDetailsModal;
